import { useCallback, useState } from 'react';

import { SelectedMedia } from '@pletly/common';
import { useOrganisationInfo } from '@pletly/feature-organisation';
import { useUser } from '%hooks';
import { startPublishPostTask } from '%helpers/backgroundTask';
import { PrivacyStatus } from '%constants/PrivacyStatus';
import { NewPostScreenParams } from './types';
import { PostMutationsOptions } from './mutations/postMutations';

export const useSavePost = ({
	id,
	informationFeedId,
	communityGroupUuid,
}: Pick<NewPostScreenParams, 'id' | 'informationFeedId' | 'communityGroupUuid' | 'type'>) => {
	const user = useUser();
	const [isSaving, setIsSaving] = useState(false);
	const { organisationInfo } = useOrganisationInfo();

	const savePostInBackground = useCallback(
		async (
			{
				selectedMedia,
				content,
				selectedUsers,
				organisationTeamsIds,
				selectedAllStaff,
				isInternal = false,
				publishAt,
			}: {
				selectedMedia: SelectedMedia[];
				content: string;
				selectedUsers?: string[];
				organisationTeamsIds?: string[];
				selectedAllStaff?: boolean;
				isInternal?: boolean;
				publishAt?: Date | null;
			},
			options?: PostMutationsOptions,
		) => {
			if (isSaving) {
				return;
			}

			setIsSaving(true);

			try {
				const organisationUuid = selectedAllStaff ? organisationInfo?.uuid : undefined;
				await startPublishPostTask(
					{
						content,
						selectedMedia,
						selectedUsers,
						author: user,
						id,
						informationFeedId,
						communityGroupUuid,
						organisationTeamsIds,
						organisationUuid,
						status: selectedAllStaff
							? PrivacyStatus.OrganisationAllEmployees
							: organisationTeamsIds
								? PrivacyStatus.OrganisationTeams
								: isInternal
									? PrivacyStatus.SelectedUsersStaff
									: PrivacyStatus.SelectedUsers,
						publishAt,
					},
					options,
				);
			} catch (error) {
				console.error(error);
			} finally {
				setIsSaving(false);
			}
		},
		[isSaving, organisationInfo?.uuid, user, id, informationFeedId, communityGroupUuid],
	);

	return {
		savePost: savePostInBackground,
		saving: isSaving,
	};
};
